import { z } from 'zod';

/**
 * Requirement #17 — Shift-Right Production Quality Intelligence.
 * Correlate production signals into the SAME quality_change_reviews artefact.
 * Advisory only — never auto-rewrites or auto-executes.
 */

const IMPACT_LEVELS = ['none', 'low', 'medium', 'high', 'critical'] as const;
const IMPACT_CONFIDENCE = ['low', 'medium', 'high'] as const;

export const PRODUCTION_SIGNAL_KINDS = [
  'production_defect',
  'incident',
  'error_fingerprint',
  'api_error',
  'ui_error',
  'performance',
] as const;
export type ProductionSignalKind = (typeof PRODUCTION_SIGNAL_KINDS)[number];

export const PRODUCTION_INTEL_STATES = [
  'new',
  'investigating',
  'correlated',
  'review_required',
  'retest_required',
  'resolved',
  'dismissed',
] as const;
export type ProductionIntelState = (typeof PRODUCTION_INTEL_STATES)[number];

export const PRODUCTION_RECOMMENDATIONS = [
  'manual_review',
  'automation_review',
  'retest',
  'regression',
  'maintenance',
  'no_action',
] as const;
export type ProductionRecommendation = (typeof PRODUCTION_RECOMMENDATIONS)[number];

export const ProductionSignalIngestSchema = z.object({
  kind: z.enum(PRODUCTION_SIGNAL_KINDS).default('incident'),
  title: z.string().trim().min(1).max(500),
  summary: z.string().trim().max(2_000).optional().default(''),
  severity: z.enum(['low', 'medium', 'high', 'critical']).optional().default('medium'),
  environment: z.string().trim().max(80).optional().default('production'),
  /** Optional deploy / release SHA for correlation with change reviews. */
  commitSha: z.string().trim().min(7).max(64).optional(),
  release: z.string().trim().max(120).optional(),
  endpoint: z.string().trim().max(500).optional(),
  route: z.string().trim().max(500).optional(),
  component: z.string().trim().max(200).optional(),
  fingerprint: z.string().trim().max(500).optional(),
  externalId: z.string().trim().max(200).optional(),
  externalUrl: z.string().trim().max(1_000).optional(),
  stackTrace: z.string().max(8_000).optional(),
  firstSeenAt: z.string().datetime().optional(),
  lastSeenAt: z.string().datetime().optional(),
  frequency: z.number().int().nonnegative().optional(),
  /** Link to existing agent_findings row when correlating an ingested Sentry issue. */
  findingId: z.string().uuid().optional(),
});
export type ProductionSignalIngest = z.input<typeof ProductionSignalIngestSchema>;

export const ProductionEvidenceSchema = z.object({
  code: z.string().max(80),
  message: z.string().max(500),
  weight: z.number().min(0).max(100).optional(),
});
export type ProductionEvidence = z.infer<typeof ProductionEvidenceSchema>;

/** Stored in quality_change_reviews.findings.production — no second impact table. */
export const ProductionIntelSchema = z.object({
  signalId: z.string().max(80),
  findingId: z.string().uuid().nullable().optional(),
  kind: z.enum(PRODUCTION_SIGNAL_KINDS),
  title: z.string().max(500),
  summary: z.string().max(2_000).default(''),
  severity: z.enum(['low', 'medium', 'high', 'critical']),
  environment: z.string().max(80).default('production'),
  commitSha: z.string().max(64).nullable().optional(),
  release: z.string().max(120).nullable().optional(),
  endpoint: z.string().max(500).nullable().optional(),
  route: z.string().max(500).nullable().optional(),
  component: z.string().max(200).nullable().optional(),
  fingerprint: z.string().max(500).nullable().optional(),
  externalId: z.string().max(200).nullable().optional(),
  externalUrl: z.string().max(1_000).nullable().optional(),
  state: z.enum(PRODUCTION_INTEL_STATES).default('new'),
  recommendation: z.enum(PRODUCTION_RECOMMENDATIONS).default('no_action'),
  impactLevel: z.enum(IMPACT_LEVELS).default('none'),
  confidence: z.enum(IMPACT_CONFIDENCE).default('low'),
  evidence: z.array(ProductionEvidenceSchema).max(40).default([]),
  impactedFlows: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        score: z.number().min(0).max(1).optional(),
        automationFirst: z.boolean().optional(),
      }),
    )
    .max(100)
    .default([]),
  impactedScenarios: z
    .array(z.object({ id: z.string().uuid(), name: z.string() }))
    .max(100)
    .default([]),
  impactedRequirements: z
    .array(z.object({ id: z.string().uuid(), key: z.string(), name: z.string().optional() }))
    .max(100)
    .default([]),
  relatedFailureKeys: z.array(z.string().max(40)).max(20).default([]),
  risk: z
    .object({
      riskLevel: z.enum(['low', 'medium', 'high', 'critical']),
      riskScore: z.number().min(0).max(100),
      confidence: z.enum(['low', 'medium', 'high']),
    })
    .nullable()
    .optional(),
  decision: z
    .object({
      action: z.enum(['investigate', 'review', 'retest', 'resolve', 'dismiss']),
      note: z.string().max(2_000).optional(),
      decidedAt: z.string(),
      decidedBy: z.string().uuid().nullable().optional(),
      retestRunIds: z.array(z.string().uuid()).max(50).optional(),
    })
    .nullable()
    .optional(),
  /** Explicit locks — #17 never auto-rewrites or auto-executes on ingest/correlate. */
  rewritten: z.literal(false).default(false),
  executed: z.literal(false).default(false),
  aiAdvisoryOnly: z.literal(true).default(true),
});
export type ProductionIntel = z.infer<typeof ProductionIntelSchema>;

export const ProductionCorrelateRequestSchema = ProductionSignalIngestSchema;
export type ProductionCorrelateRequest = z.input<typeof ProductionCorrelateRequestSchema>;

export const ProductionCorrelateResponseSchema = z.object({
  projectId: z.string().uuid(),
  reviewId: z.string().uuid().nullable(),
  commitSha: z.string(),
  production: ProductionIntelSchema,
  /** Always false — correlate never starts runs. */
  executed: z.literal(false),
  rewritten: z.literal(false),
});
export type ProductionCorrelateResponse = z.infer<typeof ProductionCorrelateResponseSchema>;

export const ProductionDecisionRequestSchema = z.object({
  action: z.enum(['investigate', 'review', 'retest', 'resolve', 'dismiss']),
  note: z.string().max(2_000).optional(),
});
export type ProductionDecisionRequest = z.input<typeof ProductionDecisionRequestSchema>;

export const ProductionDecisionResponseSchema = z.object({
  reviewId: z.string().uuid(),
  production: ProductionIntelSchema,
  started: z.number().int().nonnegative().optional(),
  skipped: z.number().int().nonnegative().optional(),
  retestRunIds: z.array(z.string().uuid()).optional(),
  /** True only when human requested retest and dispatcher was invoked. */
  executed: z.boolean(),
  rewritten: z.literal(false),
});
export type ProductionDecisionResponse = z.infer<typeof ProductionDecisionResponseSchema>;

/** Pure helpers — unit-testable without DB. */
export function scrubProductionText(text: string): string {
  return text
    .replace(/\$\{SECRET\.[^}]+\}/gi, '${SECRET.REDACTED}')
    .replace(/\b(password|passwd|secret|api[_-]?key|token|bearer|authorization)\s*[:=]\s*\S+/gi, '$1=[REDACTED]')
    .replace(/Bearer\s+\S+/gi, 'Bearer [REDACTED]')
    .slice(0, 2_000);
}

export function productionSignalId(input: {
  findingId?: string | null;
  externalId?: string | null;
  fingerprint?: string | null;
  title: string;
}): string {
  if (input.findingId) return `finding:${input.findingId}`;
  if (input.externalId) return `ext:${input.externalId.slice(0, 60)}`;
  if (input.fingerprint) return `fp:${input.fingerprint.slice(0, 60)}`;
  return `title:${input.title.slice(0, 40).toLowerCase().replace(/\s+/g, '-')}`;
}

/** Synthetic review key when no deploy SHA is known (fits commit_sha 7–64). */
export function productionReviewSha(signalId: string): string {
  const compact = signalId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 28);
  const body = (compact || 'unknown').padEnd(7, '0').slice(0, 28);
  return `prod${body}`.slice(0, 64);
}

export function deriveProductionRecommendation(input: {
  flowCount: number;
  scenarioCount: number;
  manualOnly: number;
  automationOnly: number;
  both: number;
  evidenceCount: number;
}): ProductionRecommendation {
  if (input.evidenceCount === 0 && input.flowCount === 0 && input.scenarioCount === 0) {
    return 'no_action';
  }
  if (input.both > 0 || (input.manualOnly > 0 && input.automationOnly > 0)) return 'maintenance';
  if (input.manualOnly > 0 && input.automationOnly === 0) return 'manual_review';
  if (input.flowCount > 0) return 'retest';
  if (input.scenarioCount > 0) return 'manual_review';
  return 'automation_review';
}

export function deriveProductionState(recommendation: ProductionRecommendation): ProductionIntelState {
  if (recommendation === 'no_action') return 'new';
  if (recommendation === 'retest' || recommendation === 'regression') return 'retest_required';
  if (
    recommendation === 'manual_review' ||
    recommendation === 'automation_review' ||
    recommendation === 'maintenance'
  ) {
    return 'review_required';
  }
  return 'correlated';
}

export function deriveProductionConfidence(input: {
  evidenceCount: number;
  hasSha: boolean;
  maxFlowScore: number;
}): 'low' | 'medium' | 'high' {
  if (input.evidenceCount >= 3 && (input.hasSha || input.maxFlowScore >= 0.5)) return 'high';
  if (input.evidenceCount >= 2 || input.maxFlowScore >= 0.25) return 'medium';
  if (input.evidenceCount >= 1) return 'low';
  return 'low';
}
